<template>
    <div class="kb-files">
        <div class="header">
            <div class="title">{{ $t("chatbotv2.files") }}</div>
            <div class="actions">
                <SupsisButton primary @click="selectFile">
                    <i class="material-icons">add</i>
                    <span>{{ $t("chatbotv2.addFile") }}</span>
                </SupsisButton>
            </div>
        </div>
        <EmbeddingProgressPanel
            :visible="uploading || showEmbeddingBanner"
            :show-upload="uploading"
            :show-embedding="showEmbeddingBanner"
            :upload-percent="uploadProgress"
            :upload-completed="completedFiles"
            :upload-total="totalFiles"
            :embed-percent="embeddingOverlay.percent"
            :embed-completed="embeddingOverlay.completed"
            :embed-total="embeddingOverlay.total"
        />
        <div v-if="files.length">
            <div>
                <div>
                    <ve-pagination
                        :total="total"
                        :page-index="page"
                        :page-size="pageSize"
                        :page-size-option="[1, 10, 15, 20, 50, 100]"
                        @on-page-number-change="(val) => (page = val)"
                        @on-page-size-change="(val) => (pageSize = val)"
                    />
                </div>
                <div class="files">
                    <div class="file" v-for="kb in files" :key="kb._id">
                        <div v-if="isSupsisRag" class="file-embed-header">
                            <span class="file-embed-label">{{ $t("chatbotv2.embeddingStatusCardLabel") }}</span>
                            <a-tooltip
                                v-if="resolveEmbedStatus(kb) === 'failed' && kb.supsisEmbed && kb.supsisEmbed.error"
                                placement="top"
                                :title="kb.supsisEmbed.error"
                            >
                                <span :class="['file-embed-pill', 'file-embed-pill--' + resolveEmbedStatus(kb)]">
                                    <span class="file-embed-dot" />
                                    {{ getEmbedPillText(kb) }}
                                </span>
                            </a-tooltip>
                            <span
                                v-else
                                :class="['file-embed-pill', 'file-embed-pill--' + resolveEmbedStatus(kb)]"
                            >
                                <span class="file-embed-dot" />
                                {{ getEmbedPillText(kb) }}
                            </span>
                        </div>
                        <div class="file-body">
                            <div class="file-icon-wrap">
                                <div class="file-icon">
                                    <i class="material-icons">{{ getFileIconType(kb) }}</i>
                                </div>
                            </div>
                            <div class="file-name" :title="getFileName(kb)">{{ getFileName(kb) }}</div>
                        </div>
                        <div class="file-actions">
                            <a-tooltip v-if="isSupsisRag" placement="top" :title="$t('chatbotv2.reembedKnowledge')">
                                <SupsisButton
                                    small
                                    ghost
                                    class="file-action-btn"
                                    :disabled="loading || isKbEmbeddingBusy(kb)"
                                    @click="(e) => reembedFile(kb, e)"
                                >
                                    <i class="material-icons">model_training</i>
                                </SupsisButton>
                            </a-tooltip>
                            <a-tooltip placement="top" :title="$t('message.delete')">
                                <SupsisButton small ghost class="file-action-btn" :disabled="loading" @click="() => deleteFile(kb)">
                                    <i class="material-icons">delete</i>
                                </SupsisButton>
                            </a-tooltip>
                            <a-tooltip placement="top" :title="$t('chatbotv2.openFile')">
                                <SupsisButton small ghost class="file-action-btn" :disabled="loading" @click="() => openFile(kb)">
                                    <i class="material-icons">open_in_new</i>
                                </SupsisButton>
                            </a-tooltip>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <ComingSoon
            v-else
            :action-text="$t('chatbotv2.addFile')"
            :no-data-text="$t('chatbotv2.noFileAdded')"
            :addContent="selectFile"
            :disabled="false"
        />
    </div>
</template>
<script>
import Vue from "vue";
import ComingSoon from "../ComingSoon.vue";
import { getExtensionFromFileName } from "../../../../../utils/utils";
import { uploadAsset } from "../../../../../api/assetUpload";
import mimeTypes from "../../../../../components/util/mimeTypes";
import axios from "axios";
import { mapState } from "vuex";
import log2 from "../../../../../utils/log2";
import { CHATBOT_API } from "../../../../../api/chatbotv2";
import { sendSocketMessage, socketMessageStream } from "../../../../../utils/socketStream";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";
import EmbeddingProgressPanel from "../components/EmbeddingProgressPanel.vue";

const log = log2("KBFiles");
export default Vue.extend({
    name: "KBFiles",
    components: { SupsisButton, ComingSoon, EmbeddingProgressPanel },
    props: {
        storyId: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            total: 0,
            page: 1,
            pageSize: 10,
            files: [],
            uploading: false,
            uploadProgress: 0,
            loading: false,
            uploadQueue: [],
            currentUploadIndex: 0,
            totalFiles: 0,
            completedFiles: 0,
            embedPollTimer: null,
            embeddingOverlay: {
                active: false,
                total: 0,
                completed: 0,
                percent: 0,
                jobType: "",
            },
        };
    },
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
            site: (state) => state.site.site,
        }),
        isSupsisRag() {
            const story = this.$store.state.CB.stories.find((s) => s._id === this.storyId);
            return story && story.ai && story.ai.ragMethod === "supsis-rag";
        },
        hasActiveEmbeddingInList() {
            const self = this;
            return this.files.some(function (f) {
                const st = self.resolveEmbedStatus(f);
                return st === "enqueued" || st === "processing";
            });
        },
        showEmbeddingBanner() {
            return this.isSupsisRag && (this.embeddingOverlay.active || this.hasActiveEmbeddingInList);
        },
    },
    methods: {
        selectFile() {
            let fileEl = document.createElement("input");
            const supportedFileTypes = [
                "application/csv",
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                "text/html",
                "application/json",
                "text/markdown",
                "text/x-markdown",
                "application/pdf",
                "application/vnd.openxmlformats-officedocument.presentationml.presentation",
                "text/x-tex",
                "text/plain",
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                "application/xml",
                ".md", 
            ];
            fileEl.setAttribute("type", "file");
            fileEl.setAttribute("accept", supportedFileTypes.join(","));
            fileEl.setAttribute("multiple", "true");

            fileEl.onchange = async () => {
                let files = Array.from(fileEl.files);
                if (files.length > 0) {
                    this.uploadQueue = files;
                    this.totalFiles = files.length;
                    this.completedFiles = 0;
                    this.currentUploadIndex = 0;
                    this.uploading = true;
                    this.uploadProgress = 0;
                    
                    this.preventPageClose();

                    await this.uploadFilesSequentially();
                }
            };
            fileEl.click();
        },
        async uploadFilesSequentially() {
            for (let i = 0; i < this.uploadQueue.length; i++) {
                this.currentUploadIndex = i;
                const file = this.uploadQueue[i];
                
                try {
                    await this.uploadSingleFile(file);
                    this.completedFiles++;
                } catch (error) {
                    console.error(`Dosya yükleme hatası: ${file.name}`, error);
                    this.$message.error({
                        content: `${file.name} dosyası yüklenemedi`,
                        duration: 3,
                    });
                }
                
                this.uploadProgress = Math.round((this.completedFiles / this.totalFiles) * 100);
            }
            
            this.uploading = false;
            this.allowPageClose();

            this.$message.success({
                content: `${this.completedFiles} dosya başarıyla yüklendi`,
                duration: 3,
            });
            if (this.isSupsisRag) {
                this.fetch(this.page, this.pageSize);
            }
        },
        async uploadSingleFile(file) {
            const isSupsisRag = (this.$store.state.CB.stories.find((story) => story._id === this.storyId)?.ai?.ragMethod) === "supsis-rag";
            const fileSizeLimit = isSupsisRag ? 25 * 1024 * 1024 : 10 * 1024 * 1024;
            return new Promise(async (resolve, reject) => {
                if (file.size > fileSizeLimit) {
                    this.$message.error({
                        content: `${file.name} - ${this.$t("message.assetOversize")}`,
                        duration: 3,
                    });
                    reject(new Error("File too large"));
                    return;
                }

                try {
                    const fileType = getExtensionFromFileName(file.name);
                    let path = `${this.site._id}/chatbot-knowledge-base/`;

                    let { data } = await uploadAsset({
                        path,
                        mime: file.type,
                        ext: mimeTypes[file.type] || fileType || "txt",
                        name: file.name,
                        size: file.size,
                        type: "chatbot"
                    });

                    const response = await axios({
                        method: data.method,
                        url: data.url,
                        data: file,
                        headers: {
                            "Content-Type": file.type,
                        },
                    });

                    const kb = await CHATBOT_API.KNOWLEDGE_BASE.createKnowledgeBase(
                        this.storyId,
                        "file",
                        undefined,
                        data.asset._id,
                        undefined,
                        undefined
                    ).then((res) => res.data);

                    this.files.push(kb);
                    this.$emit("updated", this.files);

                    sendSocketMessage("chatbot/v2 kb-file-added", {
                        siteId: this.site._id,
                        storyId: this.storyId,
                        ids: [kb._id],
                        isAssistantAPI: !!this.$store.state.CB.stories.find((story) => story._id === this.storyId)?.ai?.assistantId,
                        ragMethod: (this.$store.state.CB.stories.find((story) => story._id === this.storyId)?.ai?.ragMethod) || "openai-rag",
                    });

                    resolve(response);
                } catch (error) {
                    reject(error);
                }
            });
        },
        preventPageClose() {
            this.beforeUnloadHandler = (event) => {
                const message = 'Dosya yükleme işlemi devam ediyor. Sayfadan ayrılmayınız!';
                event.preventDefault();
                event.returnValue = message;
                return message;
            };
            window.addEventListener('beforeunload', this.beforeUnloadHandler);
        },
        allowPageClose() {
            if (this.beforeUnloadHandler) {
                window.removeEventListener('beforeunload', this.beforeUnloadHandler);
                this.beforeUnloadHandler = null;
            }
        },
        openFile(kb) {
            const asset = kb.file.asset;
            const url = asset.v2Format && asset.cdnUrl
            ? asset.cdnUrl
            :`https://static.supsis.live/${asset.path}${asset._id}.${asset.ext}`;
            window.open(url, "_blank");
        },
        deleteFile(item) {
            this.loading = true;
            CHATBOT_API.KNOWLEDGE_BASE.deleteMultiKnowledgeBase(this.storyId, [item._id])
                .then((res) => {
                    this.$message.success({
                        content: this.$t("chatbotv2.fileDeleted"),
                        duration: 2,
                        key: "delete",
                    });
                    this.files = this.files.filter((f) => f._id !== item._id);
                    this.$emit("updated", this.files);

                    sendSocketMessage("chatbot/v2 kb-file-removed", {
                        siteId: this.site._id,
                        storyId: this.storyId,
                        ids: [item._id],
                        isAssistantAPI: !!this.$store.state.CB.stories.find((story) => story._id === this.storyId)?.ai?.assistantId,
                        ragMethod: (this.$store.state.CB.stories.find((story) => story._id === this.storyId)?.ai?.ragMethod) || "openai-rag",
                    });
                })
                .catch((err) => {
                    this.$message.error({
                        content: this.$t("chatbotv2.fileDeletedFailed"),
                        duration: 2,
                        key: "delete",
                    });
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        listenSocket() {
            const self = this;
            this.sub = socketMessageStream.subscribe(({ cmd, data }) => {
                if (cmd === "kb_embed_progress" && data && data.storyId === self.storyId && data.siteId === self.site._id) {
                    self.applyKbEmbedProgress(data);
                }
                if (cmd === "retrain_completed" && data && data.storyId === self.storyId && data.siteId === self.site._id) {
                    self.embeddingOverlay.active = false;
                    self.fetch(self.page, self.pageSize);
                    const { success, isDelete, migrateKb } = data;
                    if (migrateKb) {
                        if (success) {
                            self.$message.success({
                                content: self.$t("chatbotv2.supsisRagMigrateDone"),
                                duration: 4,
                                key: "retrain",
                            });
                        } else {
                            self.$message.error({
                                content: self.$t("chatbotv2.supsisRagMigrateFailed"),
                                duration: 4,
                                key: "retrain",
                            });
                        }
                    } else if (success) {
                        const message = isDelete
                            ? `Dosyalar silindi ve ve eğitim başlatıldı. Bu işlem bir kaç dakika sürebilir.`
                            : `Dosya yüklendi ve eğitim başlatıldı. Bu işlem bir kaç dakika sürebilir.`;
                        self.$message.success({
                            content: message,
                            duration: 3,
                            key: "retrain",
                        });
                    } else {
                        const message = isDelete
                            ? `Dosyalar silindi faka eğitim başlatılamadı. Lütfen tekrar deneyiniz.`
                            : `Dosya yüklendi fakat eğitim başlatılamadı. Lütfen tekrar deneyiniz.`;
                        const detail = data.error ? " " + data.error : "";
                        self.$message.error({
                            content: message + detail,
                            duration: 5,
                            key: "retrain",
                        });
                    }
                }
            });
        },
        applyKbEmbedProgress(payload) {
            this.embeddingOverlay.active = true;
            this.embeddingOverlay.jobType = payload.jobType || "";
            this.embeddingOverlay.total = payload.total || 0;
            this.embeddingOverlay.completed = payload.completed || 0;
            this.embeddingOverlay.percent = Math.min(100, payload.percent || 0);
            if (payload.phase === "finished") {
                const self = this;
                setTimeout(function () {
                    self.embeddingOverlay.active = false;
                }, 800);
            }
        },
        startEmbedPoll() {
            const self = this;
            this.stopEmbedPoll();
            this.embedPollTimer = setInterval(function () {
                if (self.isSupsisRag && self.hasActiveEmbeddingInList) {
                    self.fetch(self.page, self.pageSize);
                }
            }, 3000);
        },
        stopEmbedPoll() {
            if (this.embedPollTimer) {
                clearInterval(this.embedPollTimer);
                this.embedPollTimer = null;
            }
        },
        resolveEmbedStatus(kb) {
            if (!this.isSupsisRag) {
                return "idle";
            }
            if (!kb.supsisEmbed || !kb.supsisEmbed.status) {
                return "done";
            }
            return kb.supsisEmbed.status;
        },
        isKbEmbeddingBusy(kb) {
            const st = this.resolveEmbedStatus(kb);
            return st === "enqueued" || st === "processing";
        },
        reembedFile(kb, e) {
            if (e && e.stopPropagation) {
                e.stopPropagation();
            }
            if (!this.isSupsisRag) {
                return;
            }
            const story = this.$store.state.CB.stories.find((s) => s._id === this.storyId);
            const ragMethod = (story && story.ai && story.ai.ragMethod) || "openai-rag";
            sendSocketMessage("chatbot/v2 kb-reembed", {
                siteId: this.site._id,
                storyId: this.storyId,
                knowledgeBaseId: kb._id,
                type: "file",
                ragMethod: ragMethod,
            });
            this.$message.info(this.$t("chatbotv2.reembedStarted"));
        },
        getEmbedPillText(kb) {
            const st = this.resolveEmbedStatus(kb);
            const keyMap = {
                idle: "chatbotv2.embedStatusIdle",
                enqueued: "chatbotv2.embedStatusEnqueued",
                processing: "chatbotv2.embedStatusProcessing",
                done: "chatbotv2.embedStatusDone",
                failed: "chatbotv2.embedStatusFailed",
            };
            return this.$t(keyMap[st] || keyMap.done);
        },
        fetch(page, pageSize) {
            this.loading = true;
            const p = page || this.page;
            const ps = pageSize || this.pageSize;
            CHATBOT_API.KNOWLEDGE_BASE.listKnowledgeBase(this.storyId, "file", p, ps).then((res) => {
                this.files = res.data.rows;
                this.total = res.data.total;
                this.loading = false;
            });
        },
        getFileName(item) {
            const asset = item.file.asset;
            const defaultName = `${asset._id}.${asset.ext}`;

            return asset.name || defaultName;
        },
        getFileIconType(item) {
            const asset = item.file.asset;
            const ext = asset.ext;
            if (ext === "pdf") {
                return "picture_as_pdf";
            } else if (ext === "docx" || ext === "doc") {
                return "description";
            } else if (ext === "xlsx" || ext === "xls") {
                return "table_chart";
            } else if (ext === "pptx" || ext === "ppt") {
                return "slideshow";
            } else if (ext === "txt") {
                return "text_snippet";
            } else if (ext === "csv") {
                return "table_rows";
            } else if (ext === "json") {
                return "code";
            } else if (ext === "xml") {
                return "code";
            } else if (ext === "html") {
                return "code";
            } else {
                return "insert_drive_file";
            }
        },
    },
    mounted() {
        this.listenSocket();
        this.fetch();
        this.startEmbedPoll();
    },
    beforeDestroy() {
        this.sub.unsubscribe();
        this.allowPageClose();
        this.stopEmbedPoll();
    },
    watch: {
        page() {
            this.fetch(this.page, this.pageSize);
        },
        pageSize() {
            this.fetch(this.page, this.pageSize);
        },
    },
});
</script>

<style scoped lang="scss">
.kb-files {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 10px;

    .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 20px 0px;
        width: 100%;

        .title {
            color: #22272d;
            display: flex;
            font-size: 22px;
            font-weight: 600;
            line-height: 28px;
            align-items: center;
        }
    }

    .files {
        display: flex;
        gap: 10px;
        flex-wrap: wrap;
        align-items: flex-start;

        .file {
            display: flex;
            flex-direction: column;
            width: 220px;
            min-height: 248px;
            background: #ffffff;
            border-radius: 16px;
            position: relative;
            border: 1px solid rgba(34, 39, 45, 0.08);
            box-shadow: 0 4px 14px rgba(34, 39, 45, 0.06);
            overflow: hidden;
            transition: box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease;

            &:hover {
                border-color: rgba(34, 39, 45, 0.18);
                box-shadow: 0 8px 24px rgba(34, 39, 45, 0.1);
                transform: translateY(-2px);
            }

            .file-embed-header {
                padding: 12px 14px 10px;
                background: linear-gradient(180deg, #fafbfc 0%, #ffffff 100%);
                border-bottom: 1px solid rgba(34, 39, 45, 0.06);
                flex-shrink: 0;
            }

            .file-embed-label {
                display: block;
                font-size: 10px;
                font-weight: 700;
                text-transform: uppercase;
                letter-spacing: 0.08em;
                color: #8c959c;
                margin-bottom:8px;
            }

            .file-embed-pill {
                display: inline-flex;
                align-items: center;
                gap: 6px;
                padding: 5px 12px;
                border-radius: 999px;
                font-size: 12px;
                font-weight: 600;
                line-height: 1.3;
                max-width: 100%;
            }

            .file-embed-dot {
                width: 7px;
                height: 7px;
                border-radius: 50%;
                flex-shrink: 0;
            }

            .file-embed-pill--idle {
                background: rgba(140, 149, 156, 0.12);
                color: #5c6570;

                .file-embed-dot {
                    background: #8c959c;
                }
            }

            .file-embed-pill--done {
                background: rgba(82, 196, 26, 0.12);
                color: #389e0d;

                .file-embed-dot {
                    background: #52c41a;
                }
            }

            .file-embed-pill--enqueued {
                background: rgba(250, 173, 20, 0.15);
                color: #ad6800;

                .file-embed-dot {
                    background: #faad14;
                }
            }

            .file-embed-pill--processing {
                background: rgba(24, 144, 255, 0.12);
                color: #096dd9;

                .file-embed-dot {
                    background: #1890ff;
                    animation: kb-embed-pulse 1.2s ease-in-out infinite;
                }
            }

            .file-embed-pill--failed {
                background: rgba(255, 77, 79, 0.12);
                color: #cf1322;

                .file-embed-dot {
                    background: #ff4d4f;
                }
            }

            .file-body {
                display: flex;
                flex-direction: column;
                flex: 1;
                align-items: center;
                justify-content: center;
                width: 100%;
                gap: 12px;
                padding: 16px 12px 12px;
                min-height: 132px;
            }

            .file-icon-wrap {
                width: 72px;
                height: 72px;
                border-radius: 16px;
                background: rgba(34, 39, 45, 0.05);
                display: flex;
                align-items: center;
                justify-content: center;
            }

            .file-icon {
                display: flex;
                justify-content: center;
                align-items: center;

                .material-icons {
                    font-size: 44px;
                    color: #22272d;
                }
            }

            .file-name {
                width: 100%;
                min-height: 40px;
                font-size: 13px;
                font-weight: 600;
                text-align: center;
                line-height: 1.35;
                color: #22272d;
                display: -webkit-box;
                -webkit-line-clamp: 2;
                line-clamp: 2;
                -webkit-box-orient: vertical;
                overflow: hidden;
            }

            .file-actions {
                display: flex;
                justify-content: center;
                align-items: center;
                gap: 6px;
                width: 100%;
                padding: 10px 12px 12px;
                margin-top: auto;
                background: rgba(34, 39, 45, 0.03);
                border-top: 1px solid rgba(34, 39, 45, 0.06);

                .file-action-btn {
                    min-width: 36px;
                    padding: 0 8px;
                }
            }
        }
    }

    @keyframes kb-embed-pulse {
        0%,
        100% {
            opacity: 1;
            transform: scale(1);
        }
        50% {
            opacity: 0.55;
            transform: scale(0.92);
        }
    }
}
</style>
